/**
 * Badge / Label — small pill for status, category, or count.
 *
 * Design system mapping (mch-label):
 *   • Pill shape, text-xs font-medium, gap-1 px-2 py-0.5
 *   • Default: bg-webex-gray-100 text-webex-gray-1000 / dark: bg-webex-gray-900 text-white
 *   • Error:   bg-webex-red-100 text-webex-red-700 / dark: bg-webex-red-900 text-webex-red-400
 *   • Hued variants (cobalt/cyan/gold/lime/mint/orange/pink/purple/slate/violet):
 *     light: bg-{hue}-10 text-{hue}-70 · dark: bg-{hue}-90 text-{hue}-40
 *
 * Renders as <span> by default; pass `as="div"` for block contexts.
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium leading-tight align-middle whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-webex-gray-100 text-webex-gray-1000 dark:bg-webex-gray-900 dark:text-white",
        error:   "bg-webex-red-100 text-webex-red-700 dark:bg-webex-red-900 dark:text-webex-red-400",
        cobalt:  "bg-webex-cobalt-100 text-webex-cobalt-700 dark:bg-webex-cobalt-900 dark:text-webex-cobalt-400",
        cyan:    "bg-webex-cyan-100 text-webex-cyan-700 dark:bg-webex-cyan-900 dark:text-webex-cyan-400",
        gold:    "bg-webex-gold-100 text-webex-gold-700 dark:bg-webex-gold-900 dark:text-webex-gold-400",
        lime:    "bg-webex-lime-100 text-webex-lime-700 dark:bg-webex-lime-900 dark:text-webex-lime-400",
        mint:    "bg-webex-mint-100 text-webex-mint-700 dark:bg-webex-mint-900 dark:text-webex-mint-400",
        orange:  "bg-webex-orange-100 text-webex-orange-700 dark:bg-webex-orange-900 dark:text-webex-orange-400",
        pink:    "bg-webex-pink-100 text-webex-pink-700 dark:bg-webex-pink-900 dark:text-webex-pink-400",
        purple:  "bg-webex-purple-100 text-webex-purple-700 dark:bg-webex-purple-900 dark:text-webex-purple-400",
        slate:   "bg-webex-slate-100 text-webex-slate-700 dark:bg-webex-slate-900 dark:text-webex-slate-400",
        violet:  "bg-webex-violet-100 text-webex-violet-700 dark:bg-webex-violet-900 dark:text-webex-violet-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  leadingIcon?: React.ReactNode
  as?: "span" | "div"
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, leadingIcon, children, as: Tag = "span", ...props }, ref) => {
    const Component = Tag as React.ElementType
    return (
      <Component
        ref={ref}
        className={cn(badgeVariants({ variant }), className)}
        {...props}
      >
        {leadingIcon}
        {children}
      </Component>
    )
  },
)
Badge.displayName = "Badge"
